/**
 * The assembler's Details panel inside the shared PanelShell. Nothing
 * selected: the current level's rooms (click to select), where the rest are,
 * and the level's true-up. A room selected: its placement (level, X / Y / Z, rotation), the
 * quarter-turn rotate of its own data in place, its dimensions, and the way
 * into the room editor, which is where the rest of the room's geometry is
 * edited.
 */

import { css } from '@emotion/react';
import { roomPlacement } from '@kaiizen/kai-floor-plan';
import {
  assemblyLevels,
  type AssemblyRoom,
  type FloorPlanAssemblerState,
  fmt,
  formatLength,
  formatSquareFeet,
  levelLabel,
  levelRooms,
  measureFloorPlanRoom,
  type OpeningView,
  roomOpeningView,
  selectableLevels,
  selectedAssemblyRoom,
  useFloorPlanAssemblerState,
  useFloorPlanPreferences,
  useTrueUpDialog,
} from '@kaiizen/kai-floor-plan-editor-core';
import { bone, ink, midtone, paper } from '../../styles';
import { useView3D } from '../../view3d';
import { BTN_ROW_STYLE, BTN_STYLE, LengthField, LevelField, NumField, PanelShell, ReadOnlyField, RotateRoomRow, SECTION_STYLE } from '../panel';
import { TrueUpDialogView } from './TrueUpDialog';

const ROOM_LIST_STYLE = css`
  label: ASSEMBLY_ROOM_LIST_STYLE;
  display: flex; flex-direction: column; gap: 4px; margin: 0; padding: 0; list-style: none;
`;

const ROOM_ROW_STYLE = css`
  label: ASSEMBLY_ROOM_ROW_STYLE;
  display: flex; align-items: baseline; justify-content: space-between; gap: 10px;
  width: 100%; padding: 8px 10px; border: 1px solid ${bone}; background: transparent;
  font: inherit; color: ${ink}; text-align: left; cursor: pointer;
  transition: border-color .12s, background .12s;
  &:hover { border-color: ${ink}; background: ${paper}; }
  small { color: ${midtone}; font-size: 12px; white-space: nowrap; }
`;

const OTHER_LEVELS_STYLE = css`
  label: ASSEMBLY_OTHER_LEVELS_STYLE;
  color: ${midtone}; font-size: 12px; margin-top: 10px;
`;

const TRUE_UP_STYLE = css`
  label: ASSEMBLY_TRUE_UP_STYLE;
  margin-top: 18px;
  > button { display: block; width: 100%; }
`;

export const assemblerHintText = (floorPlanAssemblerState: FloorPlanAssemblerState): string => {
  const { st } = floorPlanAssemblerState;
  if (st.readOnly) return 'Click a room to see its position and size.';
  if (st.selection.kind === 'room') return 'Drag to move, ↻ to rotate. Click a gap measurement to type a distance. Edit the room itself from “Edit room”.';
  return 'Click a room to select it. Drag rooms to position them relative to each other.';
};

function RoomPlacementInspector(
  { assemblyRoom, onOpenRoom }: { assemblyRoom: AssemblyRoom; onOpenRoom?: (id: string, view: OpeningView) => void },
): React.JSX.Element {
  const floorPlanAssemblerState = useFloorPlanAssemblerState();
  const { cameraAngle } = useView3D();
  const { room } = assemblyRoom;
  const placement = roomPlacement(room);
  const feet = (label: string, setKey: string, value: number, commit: (value: number) => void) => (
    <LengthField
      label={label}
      setKey={setKey}
      value={value}
      allowNegative
      onCommit={valueFeet => {
        if (valueFeet != null) commit(valueFeet);
      }}
    />
  );
  const { units } = useFloorPlanPreferences();
  return (
    <div css={SECTION_STYLE} data-testid='assembly-room-inspector'>
      <h2>{assemblyRoom.name}</h2>
      <LevelField
        id='aroom-level'
        value={placement.level}
        levels={selectableLevels(floorPlanAssemblerState.st.rooms.map(({ room }) => room))}
        onChange={level => floorPlanAssemblerState.placeSelected({ level })}
      />
      {feet('X', 'aroom-x', placement.x, x => floorPlanAssemblerState.placeSelected({ x }))}
      {feet('Y', 'aroom-y', placement.y, y => floorPlanAssemblerState.placeSelected({ y }))}
      {feet('Z (floor elevation)', 'aroom-z', placement.z, z => floorPlanAssemblerState.placeSelected({ z }))}
      <NumField
        label='Rotation (deg)'
        setKey='aroom-rotation'
        step={5}
        value={fmt(placement.rotation)}
        onCommit={raw => {
          const rotation = parseFloat(raw);
          if (Number.isFinite(rotation)) floorPlanAssemblerState.placeSelected({ rotation });
        }}
      />
      {!floorPlanAssemblerState.st.readOnly && <RotateRoomRow onRotate={floorPlanAssemblerState.rotateSelected} />}
      <ReadOnlyField id='aroom-width' label='Width' value={formatLength(room.width, units)} />
      <ReadOnlyField id='aroom-length' label='Length' value={formatLength(room.length, units)} />
      {room.height != null && <ReadOnlyField id='aroom-height' label='Height' value={formatLength(room.height, units)} />}
      {onOpenRoom && (
        <div css={BTN_ROW_STYLE} style={{ marginTop: 10 }}>
          <button
            type='button'
            css={BTN_STYLE}
            data-act='open-room'
            onClick={() => onOpenRoom(assemblyRoom.id, roomOpeningView(floorPlanAssemblerState.st, assemblyRoom.id, cameraAngle()))}
          >
            Edit room
          </button>
        </div>
      )}
    </div>
  );
}

/** The level's true-up, full width under everything else; its dialog shows the work and what it changed. */
function TrueUpMeasurements(): React.JSX.Element {
  const { units } = useFloorPlanPreferences();
  const trueUpDialog = useTrueUpDialog(units);
  return (
    <div css={TRUE_UP_STYLE}>
      <button
        type='button'
        css={BTN_STYLE}
        data-act='true-up'
        title='Nudge room sizes, positions and doorways by up to two inches so shared walls, doorways and outside walls line up exactly'
        onClick={trueUpDialog.start}
      >
        True up measurements
      </button>
      <TrueUpDialogView dialog={trueUpDialog} />
    </div>
  );
}

function LevelOverview(): React.JSX.Element {
  const floorPlanAssemblerState = useFloorPlanAssemblerState();
  const { units } = useFloorPlanPreferences();
  const { st } = floorPlanAssemblerState;
  const rooms = levelRooms(st);
  const elsewhere = st.rooms.length - rooms.length;
  return (
    <div css={SECTION_STYLE} data-testid='assembly-level-overview'>
      <h2>{levelLabel(st.level)} · {rooms.length} {rooms.length === 1 ? 'room' : 'rooms'}</h2>
      <ul css={ROOM_LIST_STYLE}>
        {rooms.map(({ id, name, room }) => (
          <li key={id}>
            <button type='button' css={ROOM_ROW_STYLE} data-testid='assembly-room-row' onClick={() => floorPlanAssemblerState.select({ kind: 'room', id })}>
              <span>{name}</span>
              <small>
                {formatLength(room.width, units)} × {formatLength(room.length, units)} · {formatSquareFeet(measureFloorPlanRoom(room).totalFloorArea)} sqft
              </small>
            </button>
          </li>
        ))}
      </ul>
      {elsewhere > 0 && (
        <div css={OTHER_LEVELS_STYLE}>
          {elsewhere} more {elsewhere === 1 ? 'room' : 'rooms'}{' '}
          on other levels ({assemblyLevels(st.rooms).filter(level => level !== st.level).map(levelLabel).join(', ')}).
        </div>
      )}
      {!st.readOnly && rooms.length > 1 && <TrueUpMeasurements key={st.level} />}
    </div>
  );
}

export interface AssemblerSidePanelProps {
  /** Host save hook: when present, the header shows the Save button. */
  onSave?: () => void;
  /** Opens the room editor for a room; shown as “Edit room” on the selected room. */
  onOpenRoom?: (id: string, view: OpeningView) => void;
}

export function AssemblerSidePanel({ onSave, onOpenRoom }: AssemblerSidePanelProps): React.JSX.Element {
  const floorPlanAssemblerState = useFloorPlanAssemblerState();
  const selected = selectedAssemblyRoom(floorPlanAssemblerState.st);
  return (
    <PanelShell
      testId='assembler-panel'
      readOnly={floorPlanAssemblerState.st.readOnly}
      canUndo={floorPlanAssemblerState.canUndo}
      canRedo={floorPlanAssemblerState.canRedo}
      onUndo={floorPlanAssemblerState.undo}
      onRedo={floorPlanAssemblerState.redo}
      onSave={onSave}
      saveDisabled={!floorPlanAssemblerState.isDirty}
      saveLabel='Save Layout'
      saveTitle={floorPlanAssemblerState.isDirty ? 'Save the room positions and levels' : 'No changes since the last save'}
      hint={assemblerHintText(floorPlanAssemblerState)}
    >
      {selected ? <RoomPlacementInspector assemblyRoom={selected} onOpenRoom={onOpenRoom} /> : <LevelOverview />}
    </PanelShell>
  );
}
